import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Table, Nav, Tab, Alert, Spinner, Form, InputGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';
import { formatPrice } from '../../utils/formatPrice';
import './AdminDashboardPage.css';

import { FaPlus, FaPen, FaTrash, FaImage, FaUsers, FaShoppingBag, FaShoppingCart, FaEnvelope, FaFileUpload, FaPhotoVideo } from 'react-icons/fa';

const AdminDashboardPage = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [images, setImages] = useState([]);
  const [services, setServices] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [imageName, setImageName] = useState('');
  const [newsletterTitle, setNewsletterTitle] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState('');
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const [frequencyType, setFrequencyType] = useState('monthly');
  const [frequencyValue, setFrequencyValue] = useState(1);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState({
    users: true,
    products: true,
    orders: true,
    subscribers: true,
    images: true,
    services: true
  });
  const [error, setError] = useState({
    users: '',
    products: '',
    orders: '',
    subscribers: '',
    images: '',
    services: ''
  });

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not admin
    if (!user || !user.isAdmin) {
      navigate('/');
      return;
    }

    fetchUsers();
    fetchProducts();
    fetchOrders();
    fetchSubscribers();
    fetchImages();
    fetchServices();
    fetchNewsletterContent();
    fetchNewsletterSettings();
  }, [user, navigate]);

  // Image management functions
  const fetchImages = async () => {
    try {
      setLoading(prev => ({ ...prev, images: true }));
      const response = await axiosInstance.get('/image');
      setImages(response);
      setError(prev => ({ ...prev, images: '' }));
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(prev => ({ ...prev, images: 'Erro ao carregar imagens.' }));
    } finally {
      setLoading(prev => ({ ...prev, images: false }));
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    if (!imageName.trim()) {
      setUploadError('Por favor, insira um nome para a imagem antes de fazer upload.');
      return;
    }

    setUploadLoading(true);
    setUploadError('');
    setUploadSuccess('');

    const formData = new FormData();
    formData.append('name', imageName);
    formData.append('image', files[0]);

    try {
      const response = await axiosInstance.put(`/image/${imageName}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.message === "Imagem carregada com sucesso") {
        setUploadSuccess(`${files.length} ${files.length === 1 ? 'imagem carregada' : 'imagens carregadas'} com sucesso!`);
        fetchImages(); // Refresh the image list
        setImageName(''); // Limpar o nome da imagem após o sucesso

        // Auto-hide success message after 3 seconds
        setTimeout(() => setUploadSuccess(''), 3000);
      } else {
        setUploadError('Imagens não carregadas. Por favor tente novamente.');
      }
    } catch (err) {
      console.error('Error uploading images:', err);
      setUploadError('Erro ao carregar imagens. Por favor tente novamente.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteImage = async (imageName) => {
    if (window.confirm('Tem a certeza que deseja eliminar esta imagem?')) {
      try {
        await axiosInstance.delete(`/image/${imageName}`);
        setImages(images.filter(img => img.name !== imageName));
      } catch (err) {
        console.error('Error deleting image:', err);
        setError(prev => ({ ...prev, images: 'Erro ao eliminar imagem.' }));
      }
    }
  };

  const fetchNewsletterSettings = async () => {
    try {
      const response = await axiosInstance.get('/newsletter/settings');
      setNewsletterEnabled(response.enabled);
      setFrequencyType(response.frequencyType);
      setFrequencyValue(response.frequencyValue);
    } catch (err) {
      console.error('Error fetching newsletter settings:', err);
      setError(prev => ({ ...prev, newsletter: 'Erro ao carregar configurações da newsletter.' }));
    }
  };

  const handleSaveNewsletterSettings = async (overrides = {}) => {
    const payload = {
      enabled: overrides.enabled ?? newsletterEnabled,
      frequencyType: overrides.frequencyType ?? frequencyType,
      frequencyValue: overrides.frequencyValue ?? frequencyValue
    };
    try {
      setSettingsSaving(true);
      const response = await axiosInstance.put('/newsletter/settings', payload);
      setNewsletterEnabled(response.enabled);
      setFrequencyType(response.frequencyType);
      setFrequencyValue(response.frequencyValue);
      setError(prev => ({ ...prev, newsletter: '' }));
    } catch (err) {
      console.error('Error updating newsletter settings:', err);
      setError(prev => ({ ...prev, newsletter: 'Erro ao atualizar configurações da newsletter.' }));
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleEnabledToggle = (e) => {
    const enabled = e.target.checked;
    setNewsletterEnabled(enabled);
    handleSaveNewsletterSettings({ enabled });
  };

  const handleFrequencyTypeChange = (e) => {
    const type = e.target.value;
    const defaultValue = 1;
    setFrequencyType(type);
    setFrequencyValue(defaultValue);
    handleSaveNewsletterSettings({ frequencyType: type, frequencyValue: defaultValue });
  };

  const handleFrequencyValueChange = (e) => {
    const value = Number(e.target.value);
    setFrequencyValue(value);
    handleSaveNewsletterSettings({ frequencyValue: value });
  };

  const fetchNewsletterPreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await axiosInstance.get('/newsletter/preview');
      setPreviewTitle(response.title);
      setPreviewHtml(response.html);
    } catch (err) {
      console.error('Error fetching newsletter preview:', err);
      setError(prev => ({ ...prev, newsletter: 'Erro ao carregar pré-visualização da newsletter.' }));
    } finally {
      setPreviewLoading(false);
    }
  };


  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/user');
      setUsers(response);
      setError(prev => ({ ...prev, users: '' }));
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(prev => ({ ...prev, users: 'Erro ao carregar utilizadores.' }));
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/product');
      setProducts(response.rows);
      setError(prev => ({ ...prev, products: '' }));
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(prev => ({ ...prev, products: 'Erro ao carregar produtos.' }));
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axiosInstance.get('/order');
      setOrders(response);
      setError(prev => ({ ...prev, orders: '' }));
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(prev => ({ ...prev, orders: 'Erro ao carregar encomendas.' }));
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axiosInstance.get('/newsletter/subscribers');
      setSubscribers(response);
      setError(prev => ({ ...prev, subscribers: '' }));
      return response;
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(prev => ({ ...prev, subscribers: 'Erro ao carregar subscritores.' }));
      return null;
    } finally {
      setLoading(prev => ({ ...prev, subscribers: false }));
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(prev => ({ ...prev, services: true }));
      const response = await axiosInstance.get('/service');
      setServices(response);
      setError(prev => ({ ...prev, services: '' }));
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(prev => ({ ...prev, services: 'Erro ao carregar serviços.' }));
    } finally {
      setLoading(prev => ({ ...prev, services: false }));
    }
  };

  const fetchNewsletterContent = async () => {
    try {
      setNewsletterLoading(true);
      const response = await axiosInstance.get('/newsletter/content');
      setNewsletterContent(response.value);
      setNewsletterTitle(response.title);
      setNewsletterError('');
    } catch (err) {
      console.error('Error fetching newsletter content:', err);
      setNewsletterError('Erro ao carregar conteúdo da newsletter.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleNewsletterContentChange = (e) => {
    setNewsletterContent(e.target.value);
  };

  const handleNewsletterTitleChange = (e) => {
    setNewsletterTitle(e.target.value);
  }

  const handleNewsletterSave = async () => {
    try {
      setNewsletterLoading(true);
      await axiosInstance.put('/newsletter/content', { value: newsletterContent, title: newsletterTitle });
      setNewsletterSuccess('Conteúdo da newsletter guardado com sucesso!');
      setTimeout(() => setNewsletterSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving newsletter content:', err);
      setNewsletterError('Erro ao guardar conteúdo da newsletter.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleNewsletterSend = async () => {
    if (!newsletterTitle.trim() || !newsletterContent.trim()) {
      setNewsletterError('Por favor, preencha o título e o conteúdo da newsletter antes de enviar.');
      return;
    }

    // Contagem fresca do servidor: a lista em memória pode estar por carregar
    // ou desatualizada, e não pode ser ela a bloquear um envio válido.
    const latest = await fetchSubscribers();
    const verifiedCount = (latest || subscribers).filter(sub => sub.isVerified).length;

    if (!window.confirm(`Tem a certeza que deseja enviar a newsletter para ${verifiedCount} subscritores verificados?`)) {
      return;
    }

    try {
      setNewsletterSending(true);
      setNewsletterError('');

      // Grava antes de enviar: o preview lê o rascunho guardado, por isso sem
      // isto pré-visualizava-se uma versão e enviava-se outra.
      await axiosInstance.put('/newsletter/content', { value: newsletterContent, title: newsletterTitle });

      const response = await axiosInstance.post('/newsletter/send', {
        subject: newsletterTitle,
        html: newsletterContent
      });

      const message = response.errorCount > 0
        ? `Newsletter enviada! ${response.successCount} enviados com sucesso, ${response.errorCount} falharam.`
        : `Newsletter enviada com sucesso para ${response.successCount} subscritores!`;
      setNewsletterSuccess(message);
      setTimeout(() => setNewsletterSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending newsletter:', err);
      setNewsletterError(err.response?.data?.message || 'Erro ao enviar a newsletter.');
    } finally {
      setNewsletterSending(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem a certeza que deseja eliminar este utilizador?')) {
      try {
        await axiosInstance.delete(`/user/${userId}`);
        setUsers(users.filter(user => user.id !== userId));
      } catch (err) {
        console.error('Error deleting user:', err);
        setError(prev => ({ ...prev, users: 'Erro ao eliminar utilizador.' }));
      }
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Tem a certeza que deseja eliminar este produto?')) {
      try {
        await axiosInstance.delete(`/product/${productId}`);
        setProducts(products.filter(product => product.id !== productId));
      } catch (err) {
        console.error('Error deleting product:', err);
        setError(prev => ({ ...prev, products: 'Erro ao eliminar produto.' }));
      }
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Tem a certeza que deseja eliminar esta encomenda?')) {
      try {
        await axiosInstance.delete(`/order/${orderId}`);
        setOrders(orders.filter(order => order.id !== orderId));
      } catch (err) {
        console.error('Error deleting order:', err);
        setError(prev => ({ ...prev, orders: 'Erro ao eliminar encomenda.' }));
      }
    }
  };

  const handleDeleteSubscriber = async (subscriberEmail) => {
    if (window.confirm('Tem a certeza que deseja eliminar este subscritor?')) {
      try {
        await axiosInstance.delete(`/newsletter/${encodeURIComponent(subscriberEmail)}`);
        setSubscribers(subscribers.filter(subscriber => subscriber.email !== subscriberEmail));
      } catch (err) {
        console.error('Error deleting subscriber:', err);
        setError(prev => ({ ...prev, subscribers: 'Erro ao eliminar subscritor.' }));
      }
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Tem a certeza que deseja eliminar este serviço?')) {
      try {
        await axiosInstance.delete(`/service/${serviceId}`);
        setServices(services.filter(service => service.id !== serviceId));
      } catch (err) {
        console.error('Error deleting service:', err);
        setError(prev => ({ ...prev, services: 'Erro ao eliminar serviço.' }));
      }
    }
  };

  return (
    <Container fluid className="py-4">
      <h1 className="mb-4">Painel de Administração</h1>

      <Tab.Container defaultActiveKey="users">
        <Row>
          <Col md={3} lg={2} className="mb-4">
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Menu de Administração</h5>
              </Card.Header>
              <Card.Body className="p-0">
                <Nav variant="pills" className="flex-column my-custom-nav">
                  <Nav.Item>
                    <Nav.Link eventKey="users" className='nav-link'>
                      <FaUsers className="me-2" /> Utilizadores
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="products" className='nav-link'>
                      <FaShoppingBag className="me-2" /> Produtos
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="services" className='nav-link'>
                      <FaShoppingBag className="me-2" /> Serviços
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="orders" className='nav-link'>
                      <FaShoppingCart className="me-2" /> Encomendas
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="subscribers" className='nav-link'>
                      <FaEnvelope className="me-2" /> Subscritores
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="media" className='nav-link'>
                      <FaPhotoVideo className="me-2" /> Biblioteca de Media
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="newsletter" className='nav-link'>
                      <FaEnvelope className="me-2" /> Newsletter
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>

          <Col md={9} lg={10}>
            <Tab.Content>
              <Tab.Pane eventKey="users">
                <Card className="shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestão de Utilizadores</h5>
                    <Button
                      as={Link}
                      to="/admin-dashboard/create-user"
                      variant="success"
                      size="sm"
                      style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                    >
                      <FaPlus className="me-2" />Novo Utilizador
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {error.users && <Alert variant="danger">{error.users}</Alert>}

                    {loading.users ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Username</th>
                              <th>Email</th>
                              <th>Admin</th>
                              <th>Estado</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center">Nenhum utilizador encontrado.</td>
                              </tr>
                            ) : (
                              users.map(user => (
                                <tr key={user.id}>
                                  <td>{user.id}</td>
                                  <td>{user.username}</td>
                                  <td>{user.email}</td>
                                  <td>{user.isAdmin ? 'Sim' : 'Não'}</td>
                                  <td>{user.isBanned ? 'Banido' : 'Ativo'}</td>
                                  <td>
                                    <Button
                                      as={Link}
                                      to={`/admin-dashboard/edit-user/${user.id}`}
                                      variant="primary"
                                      size="sm"
                                      className="me-2"
                                    >
                                      <FaPen />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteUser(user.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="products">
                <Card className="shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestão de Produtos</h5>
                    <Button
                      as={Link}
                      to="/admin-dashboard/create-product"
                      variant="success"
                      size="sm"
                      style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                    >
                      <FaPlus className="me-2" />Novo Produto
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {error.products && <Alert variant="danger">{error.products}</Alert>}

                    {loading.products ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Imagem</th>
                              <th>Nome</th>
                              <th>Preço</th>
                              <th>Stock</th>
                              <th>Digital</th>
                              <th>Ordem</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center">Nenhum produto encontrado.</td>
                              </tr>
                            ) : (
                              products.map(product => (
                                <tr key={product.id}>
                                  <td>{product.id}</td>
                                  <td>
                                    {product.images && product.images[0] ? (
                                      <img
                                        src={product.images[0].src}
                                        alt={product.name}
                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: '50px',
                                          height: '50px',
                                          background: colors.light,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px solid #ddd'
                                        }}
                                      >
                                        <FaImage className="text-muted" />
                                      </div>
                                    )}
                                  </td>
                                  <td>{product.name}</td>
                                  <td>{formatPrice(product.price)}€</td>
                                  <td>{product.stock}</td>
                                  <td>{product.isDigital ? 'Sim' : 'Não'}</td>
                                  <td>{product.order}</td>
                                  <td>
                                    <Button
                                      as={Link}
                                      to={`/admin-dashboard/edit-product/${product.id}`}
                                      variant="primary"
                                      size="sm"
                                      className="me-2"
                                    >
                                      <FaPen />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteProduct(product.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="services">
                <Card className="shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestão de Serviços</h5>
                    <Button
                      as={Link}
                      to="/admin-dashboard/create-service"
                      variant="success"
                      size="sm"
                      style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                    >
                      <FaPlus className="me-2" />Novo Serviço
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {error.services && <Alert variant="danger">{error.services}</Alert>}

                    {loading.services ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Imagem</th>
                              <th>Nome</th>
                              <th>Ordem</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {services.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center">Nenhum serviço encontrado.</td>
                              </tr>
                            ) : (
                              services.map(service => (
                                <tr key={service.id}>
                                  <td>{service.id}</td>
                                  <td>
                                    {service.image ? (
                                      <img
                                        src={service.image}
                                        alt={service.name}
                                        style={{ 
                                          width: '50px', 
                                          height: '50px', 
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                          border: '1px solid #ddd'
                                        }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: '50px',
                                          height: '50px',
                                          background: '#f0f0f0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '4px',
                                          border: '1px solid #ddd'
                                        }}
                                      >
                                        <FaImage style={{ color: '#ccc' }} />
                                      </div>
                                    )}
                                  </td>
                                  <td>{service.name}</td>
                                  <td>{service.order}</td>
                                  <td>
                                    <Button
                                      as={Link}
                                      to={`/admin-dashboard/edit-service/${service.id}`}
                                      variant="primary"
                                      size="sm"
                                      className="me-2"
                                    >
                                      <FaPen />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteService(service.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="orders">
                <Card className="shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestão de Encomendas</h5>
                    <Button
                      as={Link}
                      to="/admin-dashboard/create-order"
                      variant="success"
                      size="sm"
                      style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                    >
                      <FaPlus className="me-2" />Nova Encomenda
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {error.orders && <Alert variant="danger">{error.orders}</Alert>}

                    {loading.orders ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Cliente ID</th>
                              <th>Data</th>
                              <th>Total</th>
                              <th>Estado</th>
                              <th>Método Pagamento</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center">Nenhuma encomenda encontrada.</td>
                              </tr>
                            ) : (
                              orders.map(order => (
                                <tr key={order.id}>
                                  <td>{order.id}</td>
                                  <td>{order.user_id}</td>
                                  <td>{new Date(order.order_date).toLocaleDateString('pt-PT')}</td>
                                  <td>{formatPrice(order.total_price_with_tax)}€</td>
                                  <td>
                                    <span className={`badge bg-${order.status === 'delivered' ? 'success' :
                                      order.status === 'shipped' ? 'primary' :
                                        order.status === 'processing' ? 'info' :
                                          order.status === 'cancelled' ? 'danger' :
                                            'warning'
                                      }`}>
                                      {order.status}
                                    </span>
                                  </td>
                                  <td>{order.payment_method}</td>
                                  <td>
                                    <Button
                                      as={Link}
                                      to={`/admin-dashboard/edit-order/${order.id}`}
                                      variant="primary"
                                      size="sm"
                                      className="me-2"
                                    >
                                      <FaPen />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteOrder(order.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="subscribers">
                <Card className="shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Gestão de Subscritores</h5>
                  </Card.Header>
                  <Card.Body>
                    {error.subscribers && <Alert variant="danger">{error.subscribers}</Alert>}

                    {loading.subscribers ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Email</th>
                              <th>Estado</th>
                              <th>Data de Subscrição</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscribers.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center">Nenhum subscritor encontrado.</td>
                              </tr>
                            ) : (
                              subscribers.map(subscriber => (
                                <tr key={subscriber.id}>
                                  <td>{subscriber.id}</td>
                                  <td>{subscriber.email}</td>
                                  <td>
                                    {subscriber.unsubscribedAt
                                      ? 'Cancelado'
                                      : subscriber.isVerified
                                        ? 'Confirmado'
                                        : 'Por confirmar'}
                                  </td>
                                  <td>{new Date(subscriber.createdAt).toLocaleDateString('pt-PT')}</td>
                                  <td>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteSubscriber(subscriber.email)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* New Tab Pane for Media Management */}
              <Tab.Pane eventKey="media">
                <Card className="shadow-sm">
                  <Card.Header>
                    <h5 className="mb-0">Gestão de Media</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Image Name Input */}
                    <Form.Group className="mb-3">
                      <Form.Label>Nome da Imagem</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Insira o nome para a imagem"
                          value={imageName}
                          onChange={(e) => setImageName(e.target.value)}
                          required
                          isInvalid={uploadError && !imageName.trim()}
                        />
                        <InputGroup.Text>
                          <small>.jpg, .png, etc.</small>
                        </InputGroup.Text>
                        {uploadError && !imageName.trim() && (
                          <Form.Control.Feedback type="invalid">
                            Por favor, insira um nome para a imagem.
                          </Form.Control.Feedback>
                        )}
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Este será o nome do arquivo no servidor. Não inclua a extensão.
                      </Form.Text>
                    </Form.Group>

                    {/* Image Upload Area */}
                    <div
                      className={`upload-area mb-4 p-4 text-center ${dragActive ? 'drag-active' : ''}`}
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                      style={{
                        border: `2px dashed ${dragActive ? colors.secondary : colors.secondary}`,
                        borderRadius: '8px',
                        backgroundColor: dragActive ? `${colors.secondary}1A` : colors.light,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minHeight: '200px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                      onClick={openFileDialog}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                      />

                      <FaFileUpload style={{ fontSize: '3rem', color: colors.secondary, marginBottom: '1rem' }} />

                      <h5 style={{ color: colors.dark }}>
                        {dragActive
                          ? 'Solte as imagens aqui'
                          : 'Arraste e solte imagens ou clique para selecionar'}
                      </h5>

                      <p style={{ color: colors.textGray }}>
                        Formatos suportados: JPG, PNG, GIF, SVG
                      </p>

                      <Button
                        style={{
                          borderColor: colors.secondary,
                          backgroundColor: colors.light,
                          color: colors.secondary
                        }}
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFileDialog();
                        }}
                      >
                        Selecionar Ficheiros
                      </Button>
                    </div>

                    {/* Upload Status Messages */}
                    {uploadLoading && (
                      <div className="text-center my-3">
                        <Spinner animation="border" role="status" size="sm" className="me-2" />
                        <span>A carregar imagens...</span>
                      </div>
                    )}

                    {uploadError && (
                      <Alert variant="danger" className="my-3" onClose={() => setUploadError('')} dismissible>
                        {uploadError}
                      </Alert>
                    )}

                    {uploadSuccess && (
                      <Alert variant="success" className="my-3" onClose={() => setUploadSuccess('')} dismissible>
                        {uploadSuccess}
                      </Alert>
                    )}

                    {/* Images List */}
                    <h5 className="mt-4 mb-3">Biblioteca de Imagens</h5>
                    {error.images && <Alert variant="danger">{error.images}</Alert>}

                    {loading.images ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">A carregar...</span>
                        </Spinner>
                      </div>
                    ) : images.length === 0 ? (
                      <p className="text-center text-muted my-5">Nenhuma imagem encontrada. Faça upload de imagens para começar.</p>
                    ) : (
                      <div className="table-responsive">
                        <Table hover>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '80px' }}>Miniatura</th>
                              <th>Nome</th>
                              <th>Dimensões</th>
                              <th>Tamanho</th>
                              <th>Data de Upload</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {images.map(image => (
                              <tr key={image.id}>
                                <td>
                                  <div
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      overflow: 'hidden',
                                      borderRadius: '4px',
                                      border: `1px solid ${colors.secondary}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <img
                                      src={image.src}
                                      alt={image.name}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                      }}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span>{image.name}</span>
                                  </div>
                                </td>
                                <td>
                                  {image.width && image.height
                                    ? `${image.width} x ${image.height} px`
                                    : 'Dimensões indisponíveis'}
                                </td>
                                <td>{formatBytes(image.size)}</td>
                                <td>{new Date(image.createdAt).toLocaleString()}</td>
                                <td>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 me-3 text-primary"
                                    onClick={() => navigator.clipboard.writeText(image.src)}
                                    title="Copiar URL"
                                  >
                                    <i className="fas fa-copy"></i>
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteImage(image.name)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              <Tab.Pane eventKey="newsletter">
                <Card className="shadow-sm">
                  <Card.Header>
                    <h5 className="mb-0">Gestão de Newsletter</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Newsletter Ativa (enviar automaticamente)"
                        checked={newsletterEnabled}
                        onChange={handleEnabledToggle}
                        disabled={settingsSaving}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Frequência de Envio</Form.Label>
                      <Form.Select
                        value={frequencyType}
                        onChange={handleFrequencyTypeChange}
                        disabled={settingsSaving}
                        className="mb-2"
                      >
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensal</option>
                      </Form.Select>

                      {frequencyType === 'weekly' ? (
                        <Form.Select value={frequencyValue} onChange={handleFrequencyValueChange} disabled={settingsSaving}>
                          <option value={0}>Domingo</option>
                          <option value={1}>Segunda-feira</option>
                          <option value={2}>Terça-feira</option>
                          <option value={3}>Quarta-feira</option>
                          <option value={4}>Quinta-feira</option>
                          <option value={5}>Sexta-feira</option>
                          <option value={6}>Sábado</option>
                        </Form.Select>
                      ) : (
                        <Form.Select value={frequencyValue} onChange={handleFrequencyValueChange} disabled={settingsSaving}>
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>{`Dia ${day}`}</option>
                          ))}
                        </Form.Select>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Título da Newsletter</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Insira o título da newsletter"
                        value={newsletterTitle}
                        onChange={handleNewsletterTitleChange}
                        required
                        isInvalid={newsletterError && !newsletterTitle.trim()}
                        className="newsletter-title mb-3"
                      />
                      {newsletterError && !newsletterTitle.trim() && (
                        <Form.Control.Feedback type="invalid" className="mb-3">
                          Por favor, insira o título da newsletter.
                        </Form.Control.Feedback>
                      )}
                      <Form.Label>Conteúdo da Newsletter</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows="5"
                        value={newsletterContent}
                        onChange={handleNewsletterContentChange}
                        required
                        isInvalid={newsletterError && !newsletterContent.trim()}
                        className="newsletter-content"
                      />
                      {newsletterError && !newsletterContent.trim() && (
                        <Form.Control.Feedback type="invalid">
                          Por favor, insira o conteúdo da newsletter.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <div className="d-flex gap-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleNewsletterSave}
                        disabled={newsletterLoading || newsletterSending}
                      >
                        {newsletterLoading ? (
                          <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                          'Guardar Newsletter'
                        )}
                      </Button>

                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleNewsletterSend}
                        disabled={newsletterLoading || newsletterSending}
                        style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                      >
                        {newsletterSending ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            A enviar...
                          </>
                        ) : (
                          <>
                            <FaEnvelope className="me-2" />
                            Enviar Newsletter
                          </>
                        )}
                      </Button>
                    </div>

                    {newsletterError && (
                      <Alert variant="danger" className="my-3" onClose={() => setNewsletterError('')} dismissible>
                        {newsletterError}
                      </Alert>
                    )}

                    {newsletterSuccess && (
                      <Alert variant="success" className="my-3" onClose={() => setNewsletterSuccess('')} dismissible>
                        {newsletterSuccess}
                      </Alert>
                    )}

                    <hr className="my-4" />
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Pré-visualização</h6>
                      <Button variant="outline-secondary" size="sm" onClick={fetchNewsletterPreview} disabled={previewLoading}>
                        {previewLoading ? <Spinner animation="border" size="sm" /> : 'Atualizar Pré-visualização'}
                      </Button>
                    </div>
                    {previewHtml ? (
                      <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '8px', backgroundColor: '#f8f9fa' }}>
                        <div className="text-muted small mb-2">Assunto: {previewTitle || '(sem título)'}</div>
                        <iframe
                          title="newsletter-preview"
                          srcDoc={previewHtml}
                          sandbox=""
                          style={{ width: '100%', minHeight: '400px', border: 'none', backgroundColor: '#fff' }}
                        />
                      </div>
                    ) : (
                      <div className="text-muted small">Clique em "Atualizar Pré-visualização" para ver o rascunho atual.</div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
}

export default AdminDashboardPage;
